import { Component, computed, input, output } from '@angular/core'
import {
  type ColDef,
  type GetRowIdFunc,
  type ICellRendererParams,
  type IHeaderParams,
  themeQuartz,
} from 'ag-grid-community'
import { AgGridAngular, type ICellRendererAngularComp, IHeaderAngularComp } from 'ag-grid-angular'
import { MatCheckbox } from '@angular/material/checkbox'
import { ColumnSelection } from './idr-columns-selector.component'

type SelectCellRendererParams = Readonly<{
  onToggle: (selection: ColumnSelection) => void
}>

const defaultRendererParams: SelectCellRendererParams = {
  onToggle: () => { /* empty */ },
}

@Component({
  imports: [MatCheckbox],
  selector: 'app-idr-columns-selector-selected',
  template: `
    <mat-checkbox [checked]="isSelected()" [disabled]="isDisabled()" (change)="onChange()"/>
  `,
})
class SelectColumnCellRenderer implements ICellRendererAngularComp {
  protected params: SelectCellRendererParams = defaultRendererParams
  protected selection: ColumnSelection | undefined

  protected isSelected (): boolean {
    return Boolean(this.selection?.selected)
  }

  protected isDisabled(): boolean {
    return !this.selection || this.selection.columnType === 'required'
  }

  protected onChange() {
    if (this.selection) {
      this.params.onToggle(this.selection)
    }
  }

  agInit(params: ICellRendererParams<ColumnSelection, string>) {
    this.refresh(params)
    this.params = params.colDef?.cellRendererParams ?? defaultRendererParams
  }

  refresh(params: ICellRendererParams<ColumnSelection>): boolean {
    this.selection = params.data
    return true
  }
}

type SelectColumnHeaderParams = Readonly<{
  hasSelections: boolean,
  isAllSelected: () => boolean,
  onChange: (selectAll: boolean) => void,
}>

const defaultHeaderParams: SelectColumnHeaderParams = {
  hasSelections: false,
  isAllSelected: () => false,
  onChange: () => { /* empty */ },
}

@Component({
  imports: [MatCheckbox],
  selector: 'app-idr-columns-selector-header',
  template: `
    @if (params.hasSelections) {
      <mat-checkbox [checked]="params.isAllSelected()" (change)="params.onChange($event.checked)"/>
    }
  `,
})
class SelectColumnHeader implements IHeaderAngularComp {
  protected params: SelectColumnHeaderParams = defaultHeaderParams

  agInit(params: IHeaderParams<ColumnSelection>) {
    this.refresh(params)
  }

  refresh(params: IHeaderParams<ColumnSelection>): boolean {
    this.params = params.column.getColDef().headerComponentParams ?? defaultHeaderParams
    return true
  }
}

const customTheme = themeQuartz.withParams({
  fontSize: 16,
  headerFontSize: 16,
})

@Component({
  imports: [AgGridAngular],
  selector: 'app-idr-columns-selector-grid',
  template: `
      <ag-grid-angular
          [defaultColDef]="defaultColDef"
          [columnDefs]="columnDefs()"
          [getRowId]="getRowId"
          [rowData]="selections()"
          [suppressNoRowsOverlay]="true"
          [suppressScrollOnNewData]="true"
          [theme]="theme"
      />
  `,
  styles: `
    ag-grid-angular {
      height: 100%;
      width: 100%;
      font-size: 16px;
    }
  `,
})
export class IdrColumnsSelectorGridComponent {
  readonly selections = input.required<ColumnSelection[]>()

  readonly selectionsChange = output<ColumnSelection[]>()

  protected readonly theme = customTheme

  protected readonly defaultColDef: ColDef = {
    sortable: false,
    resizable: false,
    suppressMovable: true,
  }

  protected readonly cellRendererParams = computed<SelectCellRendererParams>(() => ({
    onToggle: (selection) => {
      this.toggleSelection(selection)
    },
  }))

  protected readonly headerComponentParams = computed<SelectColumnHeaderParams>(() => ({
    hasSelections: this.selections().length > 0,
    isAllSelected: () => this.selections().every(_ => _.selected),
    onChange: (selectAll: boolean) => {
      if (selectAll) {
        this.handleSelectAllColumns()
      } else {
        this.handleSelectMinimalColumns()
      }
    },
  }))

  protected readonly columnDefs = computed((): ColDef[] => [
    {
      headerName: 'Column',
      field: 'label',
      flex: 1,
    },
    {
      headerName: '',
      field: 'selected',
      width: 80,
      cellRenderer: SelectColumnCellRenderer,
      cellRendererParams: this.cellRendererParams(),
      headerComponent: SelectColumnHeader,
      headerComponentParams: this.headerComponentParams(),
    },
  ])

  protected readonly getRowId: GetRowIdFunc<ColumnSelection> = ({ data }) => data.id

  protected toggleSelection (selection: ColumnSelection) {
    this.selectionsChange.emit([{ ...selection, selected: !selection.selected }])
  }

  private handleSelectAllColumns() {
    this.selectionsChange.emit(
      this.selections()
        .filter(_ => !_.selected)
        .map(s => ({ ...s, selected: true })),
    )
  }

  private handleSelectMinimalColumns() {
    this.selectionsChange.emit(
      this.selections()
        .filter(_ => _.selected && _.columnType !== 'required')
        .map(s => ({ ...s, selected: false })),
    )
  }
}
